import { useState, useEffect, useCallback } from 'react';

const OPENCV_CDN_URL = 'https://docs.opencv.org/4.9.0/opencv.js';

type LoadStatus = 'idle' | 'loading' | 'loaded' | 'error';

let globalStatus: LoadStatus = 'idle';
let globalPromise: Promise<void> | null = null;

const loadOpenCv = (): Promise<void> => {
    if (globalPromise) return globalPromise;

    if ((window as any).cv && (window as any).cv.Mat) {
        globalStatus = 'loaded';
        return Promise.resolve();
    }

    globalStatus = 'loading';
    globalPromise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = OPENCV_CDN_URL;
        script.async = true;

        script.onload = () => {
            // OpenCV.js sets cv as a Module that calls onRuntimeInitialized
            const cv = (window as any).cv;
            if (cv && cv.onRuntimeInitialized !== undefined) {
                cv.onRuntimeInitialized = () => {
                    globalStatus = 'loaded';
                    resolve();
                };
            } else if (cv && cv.Mat) {
                globalStatus = 'loaded';
                resolve();
            } else {
                // Fallback: poll for cv.Mat availability
                const poll = setInterval(() => {
                    if ((window as any).cv && (window as any).cv.Mat) {
                        clearInterval(poll);
                        globalStatus = 'loaded';
                        resolve();
                    }
                }, 100);
                setTimeout(() => {
                    clearInterval(poll);
                    if (globalStatus !== 'loaded') {
                        globalStatus = 'error';
                        reject(new Error('OpenCV.js initialization timeout'));
                    }
                }, 30000);
            }
        };

        script.onerror = () => {
            globalStatus = 'error';
            globalPromise = null;
            reject(new Error('Failed to load OpenCV.js from CDN'));
        };

        document.head.appendChild(script);
    });

    return globalPromise;
};

export const useOpenCvLoader = () => {
    const [status, setStatus] = useState<LoadStatus>(globalStatus);

    const load = useCallback(async () => {
        if (globalStatus === 'loaded') {
            setStatus('loaded');
            return;
        }
        setStatus('loading');
        try {
            await loadOpenCv();
            setStatus('loaded');
        } catch {
            setStatus('error');
        }
    }, []);

    const retry = useCallback(() => {
        globalStatus = 'idle';
        globalPromise = null;
        load();
    }, [load]);

    useEffect(() => {
        if (globalStatus === 'loaded') {
            setStatus('loaded');
        }
    }, []);

    return { status, load, retry };
};
